import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import PostCheckoutLinker from './components/PostCheckoutLinker';
import AgentsNew from './pages/AgentsNew';
import Settings from './pages/Settings';
import Executions from './pages/Executions';
import ExecutionDetail from './pages/ExecutionDetail';
import { SpansPage } from './pages/SpansPage';
import TraceDetail from './pages/TraceDetail';
import Billing from './pages/Billing';
import LinkDevice from './pages/LinkDevice';
import { Dashboard } from './pages/Dashboard';

import AgentDetailV3 from './pages/AgentDetailV3';
import Analytics from './pages/Analytics';
import Signals from './pages/Signals';
import Onboarding from './pages/Onboarding';
import OnboardingGate from './components/OnboardingGate';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Root: Show Agents page if signed in */}
          <Route
            path="/"
            element={
              <>
                <SignedIn>
                  <ProtectedRoute>
                    <OnboardingGate>
                      <AgentsNew />
                    </OnboardingGate>
                  </ProtectedRoute>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/login" replace />
                </SignedOut>
              </>
            }
          />

          {/* Auth routes */}
          <Route path="/login/*" element={<SignedOut><Login /></SignedOut>} />
          <Route path="/register/*" element={
            <>
              <SignedIn>
                <PostCheckoutLinker />
              </SignedIn>
              <SignedOut>
                <Register />
              </SignedOut>
            </>
          } />

          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={<SignedIn><ProtectedRoute><Dashboard /></ProtectedRoute></SignedIn>}
          />

          {/* Agents routes */}
          <Route
            path="/agents"
            element={<SignedIn><ProtectedRoute><AgentsNew /></ProtectedRoute></SignedIn>}
          />
          <Route
            path="/agents/:goal"
            element={<SignedIn><ProtectedRoute><AgentDetailV3 /></ProtectedRoute></SignedIn>}
          />

          {/* Settings */}
          <Route
            path="/settings"
            element={<SignedIn><ProtectedRoute><Settings /></ProtectedRoute></SignedIn>}
          />

          {/* Analytics */}
          <Route
            path="/analytics"
            element={<SignedIn><ProtectedRoute><Analytics /></ProtectedRoute></SignedIn>}
          />

          {/* Signals */}
          <Route
            path="/signals"
            element={<SignedIn><ProtectedRoute><Signals /></ProtectedRoute></SignedIn>}
          />

          {/* Traces - redirect to agents */}
          <Route
            path="/traces"
            element={<Navigate to="/agents" replace />}
          />

          {/* Legacy routes */}
          <Route
            path="/executions"
            element={<SignedIn><ProtectedRoute><Executions /></ProtectedRoute></SignedIn>}
          />
          <Route
            path="/executions/:traceId"
            element={<SignedIn><ProtectedRoute><ExecutionDetail /></ProtectedRoute></SignedIn>}
          />
          <Route
            path="/spans"
            element={<SignedIn><ProtectedRoute><SpansPage /></ProtectedRoute></SignedIn>}
          />
          <Route
            path="/traces/:traceId"
            element={<SignedIn><ProtectedRoute><TraceDetail /></ProtectedRoute></SignedIn>}
          />

          <Route
            path="/billing"
            element={<SignedIn><ProtectedRoute><Billing /></ProtectedRoute></SignedIn>}
          />

          {/* Onboarding */}
          <Route
            path="/onboarding/*"
            element={<SignedIn><ProtectedRoute><Onboarding /></ProtectedRoute></SignedIn>}
          />

          {/* Device linking */}
          <Route path="/link" element={<LinkDevice />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
